import { isEmpty } from 'lodash';
import { BaseExcelExportDialogErrors } from './types';

export interface ValidationResult {
  isValid: boolean;
  errors: BaseExcelExportDialogErrors;
}

export interface ValidationRule<T = any> {
  field: string;
  validate: (value: T, context?: any) => string[] | null;
  required?: boolean;
}

export const createValidator = <T extends Record<string, any>>(
  rules: ValidationRule<T>[]
) => (data: T, context?: any): ValidationResult => {
    const errors: BaseExcelExportDialogErrors = {};
    let isValid = true;

    rules.forEach(rule => {
      const value = data[rule.field as keyof T];
          
      if (rule.required && [undefined, null, ''].includes(value)) {
        errors[rule.field] = [`${rule.field} is required`];
        isValid = false;
        return;
      }

      if (!rule.required && [undefined, null, ''].includes(value)) {
        return;
      }

      const fieldErrors = rule.validate(value, context);
      if (fieldErrors && fieldErrors.length > 0) {
        errors[rule.field] = fieldErrors;
        isValid = false;
      }
    });

    return { isValid, errors };
  };

export const validateAtLeastOneOption = (options: Record<string, boolean>): boolean => Object.values(options).some(option => option);

export const validateRequiredSelection = (value: number | null | undefined): boolean => value !== null && value !== undefined;

export const validateFormValues = (formValues: any, hasData: Record<string, boolean>): ValidationResult => {
  const errors: BaseExcelExportDialogErrors = {};
  let isValid = true;

  const exportOptions = Object.entries(formValues)
    .filter(([key]) => key.startsWith('export_') && typeof formValues[key] === 'boolean')
    .map(([, value]) => value as boolean);

  if (!exportOptions.some(option => option)) {
    errors.general = ['At least one export option must be selected'];
    isValid = false;
  }

  const versionFields = ['cap_table', 'financial_statement', 'valuation', 'waterfall_cap_table'];
  versionFields.forEach(field => {
    const exportKey = `export_${field}`;
    if (formValues[exportKey] && hasData[field.replace('_', '')]) {
      if (!formValues[field] || formValues[field] === null) {
        errors[field] = [`${field} version must be selected`];
        isValid = false;
      }
    }
  });

  return { isValid, errors };
};

export const isFormValid = (errors: BaseExcelExportDialogErrors): boolean => isEmpty(errors);
